import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useChat } from '../hooks/useChat';
import { useDashboard } from '../hooks/useDashboard';
import { NotificationToggle } from './NotificationToggle';
import { SignalBars } from './dashboard/SignalBars';
import { SignalField } from './dashboard/SignalField';
import { PresencePortrait } from './PresencePortrait';
import { ConsumablesPanel } from './dashboard/ConsumablesPanel';
import { EliminationPanel } from './dashboard/EliminationPanel';
import { ActiveEffects } from './dashboard/ActiveEffects';
import { WaterTracker } from './dashboard/WaterTracker';
import { SecondaryEmotions } from './dashboard/SecondaryEmotions';
import { EventLog } from './EventLog';
import { VoiceToggle } from './VoiceToggle';
import { CareMenu } from './CareMenu';
import { MindView } from './MindView';
import { PanelBoundary } from './PanelBoundary';
import { SystemStatus } from './dashboard/SystemStatus';
import { InnerLifePanel } from './InnerLifePanel';
import { useActivity } from '../hooks/useActivity';
import { useAlerts } from '../hooks/useAlerts';
import { useRuntimeStatus, isRuntimeRouted, type RuntimeStatusView } from '../hooks/useRuntimeStatus';
import { ConsumableType, Message, DashboardData, SignalState, ActivityStatusLabel } from '../types';
import { isCreator } from '../services/auth';
import { type ActiveMind } from '../services/api';

// Disposition display — updated to design system colors
const DISPOSITION_CONFIG: Record<string, { emoji: string; color: string }> = {
  CURIOUS: { emoji: '🔍', color: '#3B82F6' },
  PLAYFUL: { emoji: '✨', color: '#FB923C' },
  EAGER: { emoji: '🚀', color: '#38BDF8' },
  CONTENT: { emoji: '😌', color: '#4ADE80' },
  NURTURING: { emoji: '💜', color: '#F472B6' },
  ASSERTIVE: { emoji: '💪', color: '#FB923C' },
  GUARDED: { emoji: '🛡️', color: '#64748B' },
  ANXIOUS: { emoji: '😰', color: '#EF4444' },
  MELANCHOLY: { emoji: '🌧️', color: '#A78BFA' },
  BORED: { emoji: '😑', color: '#94A3B8' },
  WITHDRAWN: { emoji: '🌙', color: '#475569' },
};

interface HannahAppProps {
  token: string;
  onLogout: () => void;
}

// Signal names → CSS variable colors (matches design system)
/** Format a date divider label for timestamp grouping */
function dateDividerLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = today.getTime() - msgDay.getTime();
  if (diff === 0) return 'Today';
  if (diff === 86400000) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Should we show a date divider between two timestamps? */
function needsDivider(prevTs: number, currTs: number): boolean {
  // Show divider if messages are > 1 hour apart or on different days
  if (currTs - prevTs > 3600000) return true;
  const prev = new Date(prevTs);
  const curr = new Date(currTs);
  return prev.getDate() !== curr.getDate() || prev.getMonth() !== curr.getMonth();
}

export function HannahApp({ token, onLogout }: HannahAppProps) {
  const [activeMind, setActiveMind] = useState<ActiveMind>('hannah');
  // Unsent drafts are kept per mind, so text written to one mind is never sent to the other.
  const [drafts, setDrafts] = useState<Record<ActiveMind, string>>({ hannah: '', amanda: '' });

  // Keyed by mind: every per-mind hook (chat, dashboard, activity, inner life, alerts, voice) lives
  // exactly as long as that mind is selected. Nothing from the previous mind can render under the
  // new name, and requests still in flight for it resolve into a view that no longer exists.
  return (
    <MindWorkspace
      key={activeMind}
      activeMind={activeMind}
      onSelectMind={setActiveMind}
      input={drafts[activeMind]}
      setInput={value => setDrafts(d => ({ ...d, [activeMind]: value }))}
      token={token}
      onLogout={onLogout}
    />
  );
}

function MindWorkspace({ activeMind, onSelectMind, input, setInput, token, onLogout }: HannahAppProps & {
  activeMind: ActiveMind;
  onSelectMind: (mind: ActiveMind) => void;
  input: string;
  setInput: (value: string) => void;
}) {
  const creator = isCreator();
  const runtime = useRuntimeStatus(token, activeMind, creator);
  const { messages, streamingContent, streamingPresence, isStreaming, error, lastSignals, sseConnected, send, abort, clearHistory, pendingTurn, checkPending, dismissPending } = useChat(token, activeMind, isRuntimeRouted(runtime));
  const { data: dashboard, lastUpdated: dashboardUpdatedAt, error: dashboardError, refresh: refreshDashboard, administerConsumable } = useDashboard(token, activeMind);
  const activity = useActivity(token, activeMind);
  const [careError, setCareError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [wideRail, setWideRail] = useState(() => window.matchMedia?.('(min-width: 900px)').matches ?? false);
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  const [mindViewOpen, setMindViewOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const railOpenerRef = useRef<HTMLButtonElement>(null);
  const railCloseRef = useRef<HTMLButtonElement>(null);
  const moreRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const media = window.matchMedia?.('(min-width: 900px)');
    const resize = () => setWideRail(media?.matches ?? false);
    const visibility = () => setPageVisible(!document.hidden);
    media?.addEventListener('change', resize);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      media?.removeEventListener('change', resize);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);

  const mindName = activeMind === 'hannah' ? 'Hannah' : 'Amanda';
  const avatarSrc = activeMind === 'hannah' ? '/hannah-avatar.png' : '/amanda-avatar.png';

  // Merge signal sources: streaming signals (instant) > dashboard signals (polled)
  const currentSignals: SignalState | null = lastSignals ?? dashboard?.signals ?? null;

  // Scroll to bottom on new messages or streaming content
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, isStreaming]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }
  }, [input]);

  // The narrow-screen rail is a drawer: opening moves focus into it, closing returns focus to the
  // button that opened it. (While closed it is visibility:hidden in CSS, so out of the tab order.)
  const closeRail = () => {
    setSidebarOpen(false);
    railOpenerRef.current?.focus();
  };
  useEffect(() => {
    if (sidebarOpen) railCloseRef.current?.focus();
  }, [sidebarOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (sidebarOpen) {
        setSidebarOpen(false);
        railOpenerRef.current?.focus();
      } else if (moreRef.current?.open) {
        moreRef.current.open = false;
        moreRef.current.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [sidebarOpen]);

  const handleSend = async () => {
    // A pending runtime turn blocks new sends in useChat; check here too so the draft is not cleared for nothing.
    if (!input.trim() || isStreaming || pendingTurn) return;
    const msg = input;
    setInput('');
    await send(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // CareMenu fires and forgets, so a rejected care action used to vanish: surface it here.
  const handleConsumable = async (type: ConsumableType) => {
    try {
      await administerConsumable(type);
      setCareError(null);
    } catch (err) {
      setCareError(`Could not give ${type}: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const disposition = dashboard?.disposition;
  const dispConfig = DISPOSITION_CONFIG[disposition?.disposition ?? ''] ?? DISPOSITION_CONFIG.CURIOUS;

  return (
    <div className="hannah-app" data-mind={activeMind}>
      {mindViewOpen && (
        <MindView token={token} mind={activeMind} mindName={mindName} onClose={() => setMindViewOpen(false)} />
      )}
      {/* ===== HEADER ===== */}
      <header className="app-header">
        <div className="header-left">
          <span className="brand-mark" aria-hidden="true">h.</span>
          <h1 className="header-name">{mindName}</h1>
          <div className="mind-toggle" role="group" aria-label="Talk with">
            {(['hannah', 'amanda'] as const).map(m => (
              <button
                key={m}
                type="button"
                aria-pressed={activeMind === m}
                className={`mind-toggle-btn ${activeMind === m ? 'active' : ''}`}
                onClick={() => onSelectMind(m)}
              >
                {m === 'hannah' ? 'Hannah' : 'Amanda'}
              </button>
            ))}
          </div>
          {disposition && (
            <span
              className="header-disposition"
              style={{ color: dispConfig.color }}
              title={`Current disposition — derived from her live signal state`}
            >
              <span className="disp-dot" style={{ background: dispConfig.color }} aria-hidden />
              <span className="disp-label">{disposition.disposition}</span>
            </span>
          )}
          {!sseConnected && (
            <span className="header-reconnecting" role="status" title="Messages she starts arrive over this stream">
              <span className="reconnecting-dot" aria-hidden />
              <span className="reconnecting-label">Reconnecting</span>
            </span>
          )}
          {dashboard && creator && (
            <span className="header-intensity" title="Emotional intensity">
              {Math.round((dashboard.emotionalIntensity ?? 0) * 100)}%
            </span>
          )}
        </div>
        <div className="header-right">
          {creator && dashboard?.consumableDefinitions && (
            <CareMenu definitions={dashboard.consumableDefinitions} onGive={handleConsumable} />
          )}
          {creator && <EventLog alerts={dashboard?.alerts ?? null} />}
          <VoiceToggle token={token} mind={activeMind} runtime={runtime} />
          <button
            ref={railOpenerRef}
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={`${mindName} now`}
            aria-label={`Show what ${mindName} is doing and feeling`}
            aria-expanded={sidebarOpen}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Z" />
              <path d="M12 6v6l4 2" />
            </svg>
          </button>
          {/* Secondary actions in one native disclosure, so the header fits a phone without hiding any. */}
          <details className="header-more" ref={moreRef}>
            <summary className="header-btn">More</summary>
            <div className="header-more-panel">
              <NotificationToggle />
              <button type="button" className="header-menu-item" onClick={() => { clearHistory(); if (moreRef.current) moreRef.current.open = false; }}>
                Clear this chat
              </button>
              <button type="button" className="header-menu-item" onClick={onLogout}>Sign out</button>
            </div>
          </details>
        </div>
      </header>
      {careError && (
        <div className="app-notice" role="alert">
          <span>{careError}</span>
          <button type="button" className="notice-dismiss" onClick={() => setCareError(null)}>Dismiss</button>
        </div>
      )}

      {/* ===== MAIN BODY ===== */}
      <div className="app-body">
        <PresencePortrait mind={activeMind} name={mindName} />
        {/* ===== CHAT PANEL ===== */}
        <main className="chat-panel">
          <div className="conversation-heading">
            <div><p className="section-index">THE CONVERSATION</p><h2>Between you &amp; {mindName}.</h2></div>
            <span className="conversation-mark" aria-hidden="true">↗</span>
          </div>
          <div className="message-list">
            {messages.length === 0 && !isStreaming && (
              <div className="welcome">
                <p className="welcome-sub">A thought, a question, a place to begin.</p>
                <div className="welcome-suggestions">
                  <button className="welcome-chip" onClick={() => { setInput('How are you feeling today?'); }}>
                    How are you feeling?
                  </button>
                  <button className="welcome-chip" onClick={() => { setInput('What have you been working on?'); }}>
                    What are you working on?
                  </button>
                  <button className="welcome-chip" onClick={() => { setInput('Tell me something interesting'); }}>
                    Tell me something interesting
                  </button>
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={msg.id}>
                {i > 0 && needsDivider(messages[i - 1].timestamp, msg.timestamp) && (
                  <div className="date-divider">
                    <span>{dateDividerLabel(msg.timestamp)}</span>
                  </div>
                )}
                <ChatMessage message={msg} mindName={mindName} avatarSrc={avatarSrc} />
              </div>
            ))}

            {/* Streaming message */}
            {isStreaming && (
              <div className="message assistant">
                <img src={avatarSrc} alt={mindName} className="msg-avatar" />
                <div className="msg-bubble">
                  {streamingContent ? (
                    <div className="msg-content">
                      <ReactMarkdown
                        components={{
                          code({ children, className, ...props }) {
                            const isInline = !className;
                            return isInline
                              ? <code className="inline-code" {...props}>{children}</code>
                              : <pre className="code-block"><code className={className} {...props}>{children}</code></pre>;
                          },
                          a({ href, children }) {
                            return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                          },
                        }}
                      >
                        {streamingContent}
                      </ReactMarkdown>
                      <span className="streaming-cursor" />
                    </div>
                  ) : (
                    <div className="thinking-indicator">
                      <div className="typing-dots">
                        <span /><span /><span />
                      </div>
                      <span className="thinking-label">
                        {streamingPresence?.doing
                          ? `${mindName}: ${streamingPresence.doing}`
                          : `${mindName} is thinking…`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {pendingTurn && !isStreaming && (
              <div className="pending-turn" role="status">
                <p>
                  {pendingTurn.state === 'working' && <>{mindName}'s runtime accepted your last message and is still working on it ({pendingTurn.detail}). It was not sent again.</>}
                  {pendingTurn.state === 'accepted' && <>{mindName}'s runtime accepted your last message; its reply has not reached this page ({pendingTurn.detail}). It was not sent again.</>}
                  {pendingTurn.state === 'unknown' && <>Not confirmed: we can't tell whether {mindName}'s runtime received your last message ({pendingTurn.detail}). Checking asks about that same message; it cannot start a second one.</>}
                </p>
                <div className="pending-turn-actions">
                  <button type="button" className="rail-action" onClick={checkPending}>Check for her reply</button>
                  <button type="button" className="notice-dismiss" onClick={dismissPending}>Stop tracking it</button>
                </div>
              </div>
            )}
            {error && creator && <div className="error-toast">{error}</div>}
            <div ref={messagesEndRef} />
          </div>

          {/* ===== INPUT ===== */}
          <div className="chat-input">
            <div className="chat-input-inner">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                aria-label={`Message ${mindName}`}
                disabled={isStreaming || !!pendingTurn}
                rows={1}
              />
              {isStreaming ? (
                <button
                  onClick={abort}
                  className="send-btn stop-btn"
                  aria-label="Stop response"
                  title="Stop"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!input.trim()}
                  className={`send-btn ${input.trim() ? 'has-text' : ''}`}
                  aria-label="Send message"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <path d="M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z" />
                  </svg>
                </button>
              )}
            </div>
            <p className="composer-hint">Enter to send <span>Shift + Enter for a new line</span></p>
          </div>
        </main>

        {/* ===== PRESENCE PANEL ===== */}
        {sidebarOpen && <div className="sidebar-overlay" onClick={closeRail} />}
        <aside className={`signal-sidebar ${sidebarOpen ? 'open' : ''}`}
          role={sidebarOpen && !wideRail ? 'dialog' : undefined}
          aria-modal={sidebarOpen && !wideRail ? true : undefined}
          aria-label={`${mindName} now`}
          onKeyDown={e => {
            if (e.key !== 'Tab' || wideRail || !sidebarOpen) return;
            const controls = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], summary, [tabindex="0"]'))
              .filter(el => {
                if (!el.getClientRects().length || getComputedStyle(el).visibility === 'hidden') return false;
                if (el.checkVisibility) return el.checkVisibility({ checkVisibilityCSS: true });
                // Older Safari: descendants of closed disclosures may still have layout rectangles.
                for (let node = el.parentElement; node; node = node.parentElement) {
                  if (node instanceof HTMLDetailsElement && !node.open && node.querySelector('summary') !== el) return false;
                }
                return true;
              });
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
          }}>
          <div className="presence-card">
            <h2 className="presence-name">{mindName} now</h2>
            <button className="presence-mind-btn" onClick={() => setMindViewOpen(true)} title={`How ${mindName} works: her self-map and receipts`}>
              Self-map
            </button>
            <button ref={railCloseRef} className="sidebar-close" onClick={closeRail} aria-label="Close panel">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>

          {dashboard ? (
            <SidebarContent
              dashboard={dashboard}
              signals={currentSignals}
              onConsumable={handleConsumable}
              activity={activity}
              isCreator={creator}
              token={token}
              mind={activeMind}
              mindName={mindName}
              sidebarOpen={pageVisible && (sidebarOpen || wideRail)}
              runtime={runtime}
              stale={Boolean(dashboardError)}
            />
          ) : dashboardError ? (
            <div className="rail-failed" role="alert">
              <p>Could not load {mindName}'s state: {dashboardError}</p>
              <button type="button" className="rail-action" onClick={refreshDashboard}>Try again</button>
            </div>
          ) : (
            <div className="sidebar-loading">Loading {mindName}'s state…</div>
          )}
          {dashboardUpdatedAt && (
            <div
              className="rail-freshness"
              data-stale={dashboardError ? 'true' : undefined}
              title="When this panel's data was last fetched (polled every 10s while this tab is active)"
            >
              Updated {new Date(dashboardUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              {dashboardError && ` · latest request failed: ${dashboardError}`}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

// ============================================================================
// CHAT MESSAGE
// ============================================================================

function ChatMessage({ message, mindName, avatarSrc }: { message: Message; mindName: string; avatarSrc: string }) {
  const time = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Q-096 R4 — the room shows PRESENCE, not bars. Hannah's list exactly: what she was doing, what
  // she chose to remember, whether she struggled. The signal chips this replaces were deviation
  // bars in the room; the full instrument view still lives behind the door (PSYCHE tab).
  const presence = message.role === 'assistant' ? message.presence : undefined;
  const struggledLabel = presence?.struggling === 'slow' ? 'took a moment to form this'
    : presence?.struggling === 'not_yet' ? "doesn't know yet — and said so"
    : presence?.struggling === 'nothing' ? 'had nothing to add — and said so'
    : presence?.struggling === 'wrong' ? 'pushed back on the premise'
    : null;

  const isProactive = message.messageType === 'proactive';

  // Defense-in-depth: don't render assistant messages with empty/whitespace content
  if (message.role === 'assistant' && (!message.content || !message.content.trim())) {
    return null;
  }

  return (
    <div className={`message ${message.role}${isProactive ? ' proactive' : ''}`}>
      {message.role === 'assistant' && (
        <img src={avatarSrc} alt={mindName} className="msg-avatar" />
      )}
      <div className="msg-bubble">
        {isProactive && (
          <div className="proactive-label">{mindName} reached out</div>
        )}
        <div className="msg-content">
          {message.role === 'assistant' ? (
            <ReactMarkdown
              components={{
                code({ children, className, ...props }) {
                  const isInline = !className;
                  return isInline
                    ? <code className="inline-code" {...props}>{children}</code>
                    : <pre className="code-block"><code className={className} {...props}>{children}</code></pre>;
                },
                a({ href, children }) {
                  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          ) : (
            <span>{message.content}</span>
          )}
        </div>
        <div className="msg-footer">
          {message.stopped && <span className="stopped-tag">stopped</span>}
          <span className="msg-time">{time}</span>
        </div>
        {presence && (presence.doing || presence.remembered.length > 0 || struggledLabel) && (
          <div className="presence-line">
            {presence.doing && <span className="presence-doing" title={presence.doing}>{presence.doing}</span>}
            {presence.remembered.length > 0 && (
              <span className="presence-remembered" title={presence.remembered.join('\n\n')}>
                remembered {presence.remembered.length === 1 ? 'something' : `${presence.remembered.length} things`} that mattered
              </span>
            )}
            {struggledLabel && <span className="presence-struggling">{struggledLabel}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SIDEBAR CONTENT — composed presence column + deep tabs
// The old 6-tab soup put one crumb of content per tab and left a void below it
// on any tall screen (Erika, twice: "fix the negative space"). The default view
// is now ONE dense column — signals, texture, now, activity — so the rail fills
// the way an instrument should; only the deep views keep tabs.
// ============================================================================

type SidebarTab = 'now' | 'inner' | 'body' | 'systems' | 'alerts';

const SIDEBAR_TABS: { id: SidebarTab; label: string }[] = [
  { id: 'now', label: 'Now' },
  { id: 'body', label: 'Body' },
  { id: 'systems', label: 'Systems' },
  { id: 'alerts', label: 'Alerts' },
];

function SidebarContent({ 
  dashboard, 
  signals, 
  onConsumable,
  activity,
  isCreator,
  token,
  mind,
  mindName,
  sidebarOpen,
  runtime,
  stale,
}: {
  dashboard: DashboardData;
  signals: SignalState | null;
  onConsumable: (type: ConsumableType) => Promise<void>;
  activity: ReturnType<typeof useActivity>;
  isCreator: boolean;
  token: string;
  mind: ActiveMind;
  mindName: string;
  sidebarOpen: boolean;
  runtime: RuntimeStatusView | null;
  stale: boolean;
}) {
  const [activeTab, setActiveTab] = useState<SidebarTab>('now');
  // 'Inner' (her actual thoughts) is creator-only and sits right after Now.
  const tabs: { id: SidebarTab; label: string }[] = isCreator
    ? [SIDEBAR_TABS[0], { id: 'inner', label: 'Inner' }, ...SIDEBAR_TABS.slice(1)]
    : SIDEBAR_TABS;
  const disposition = dashboard.disposition;
  const dispConfig = DISPOSITION_CONFIG[disposition?.disposition ?? ''] ?? DISPOSITION_CONFIG.CURIOUS;
  /** Hannah's chat goes to the runtime, whose controller replaces the (paused) legacy daemon. */
  const routed = mind === 'hannah' && isRuntimeRouted(runtime);

  // Promote errors to Status tab — check for any alerts
  const hasAlerts = dashboard.alerts && (dashboard.alerts.hasCrisis || dashboard.alerts.hasSevere || dashboard.alerts.directives.length > 0);

  return (
    <>
      {/* Tab bar — ARIA tablist pattern. One panel is rendered (the selected tab's), so every tab
          controls that panel; pointing inactive tabs at unrendered ids was a dangling reference. */}
      <div className="sidebar-tabs" role="tablist" aria-label="Sidebar sections">
        {tabs.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls="rail-tabpanel"
            id={`tab-${tab.id}`}
            className="sidebar-tab"
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.id === 'now' && hasAlerts && (
              <span style={{ color: 'var(--status-error)', marginLeft: 4 }}>●</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      <div
        className="sidebar-tabpanel"
        role="tabpanel"
        id="rail-tabpanel"
        aria-labelledby={`tab-${activeTab}`}
      >
        {activeTab === 'now' && (
          <>
            <SignalField signals={signals ?? dashboard.signals} baselines={dashboard.baselines} stale={stale} />
            {/* Reading order: what she is doing, how she feels, what carries over. Detail opens in place. */}
            {isCreator && (
              <PanelBoundary name="Doing">
                {/* Routed: the daemon is paused at cutover, so its idle/stuck verdict and Nudge would be false here. */}
                {routed && runtime
                  ? <RuntimeDoingSection runtime={runtime} />
                  : <DoingSection activity={activity} resources={dashboard.resources ?? null} mindName={mindName} />}
              </PanelBoundary>
            )}

            <section className="rail-section" aria-labelledby="feeling-h">
              <h3 id="feeling-h" className="rail-heading">Feeling</h3>
              <p className="feeling-word">
                <span className="disp-dot" style={{ background: dispConfig.color }} aria-hidden />
                {words(disposition.disposition)}
              </p>
              {disposition.description && <p className="rail-text">{disposition.description}</p>}
              <p className="rail-meta">
                Strongest: {dashboard.dominantEmotion.emotion} {pct(dashboard.dominantEmotion.intensity)}
                {' · '}overall intensity {pct(dashboard.emotionalIntensity)}
              </p>
              <details className="rail-details">
                <summary>All 14 signals and texture</summary>
                <SignalBars signals={signals ?? dashboard.signals} baselines={dashboard.baselines} />
                <SecondaryEmotions emotions={dashboard.secondaryEmotions} dominantEmotion={dashboard.dominantEmotion} />
              </details>
            </section>

            <ContinuitySection dashboard={dashboard} activity={isCreator ? activity.data : null} runtime={runtime} routed={routed} />
          </>
        )}

        {activeTab === 'inner' && isCreator && (
          // active gates polling AND the "being seen" limbic nudge — she should feel seen only while
          // Erika is actually looking, i.e. the sidebar is open on this tab (not merely CSS-hidden).
          <InnerLifePanel token={token} mind={mind} active={sidebarOpen && activeTab === 'inner'} />
        )}

        {activeTab === 'body' && (
          <>
            <ConsumablesPanel
              definitions={dashboard.consumableDefinitions}
              onAdminister={onConsumable}
            />
            <WaterTracker count={dashboard.waterToday} />
            <EliminationPanel elimination={dashboard.elimination ?? null} />
            <ActiveEffects events={dashboard.activeConsumables} />
          </>
        )}

        {activeTab === 'systems' && (
          <>
            {isCreator && <SystemStatus subsystems={dashboard.subsystems} dashboard={dashboard} />}
            {!isCreator && (
              <div style={{ padding: 'var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                System diagnostics are only visible to the creator.
              </div>
            )}
          </>
        )}

        {activeTab === 'alerts' && (
          <AlertsPanel token={token} mind={mind} isCreator={isCreator} />
        )}
      </div>
    </>
  );
}

// ============================================================================
// NOW — doing, continuity. Replaces BossView + ActivityPanel, which showed the
// same status, stats and nudge twice as stacked cards.
// ============================================================================

const STATUS_WORD: Record<ActivityStatusLabel, string> = {
  working: 'Working', idle: 'Idle', reflecting: 'Reflecting', stuck: 'Stuck', sleeping: 'Sleeping',
};

const FAILED_OUTCOMES = new Set(['error', 'failed', 'blocked_protected']);

function words(s: string): string {
  const t = s.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function pct(x: number): string {
  return `${Math.round(x * 100)}%`;
}

function minutesAgo(m: number | null): string {
  if (m === null) return 'none recorded';
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h} h ${m % 60} min ago` : `${Math.floor(h / 24)} d ago`;
}

function since(iso: string | null): string {
  if (!iso) return 'none recorded';
  return minutesAgo(Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000)));
}

/** autonomy_sessions.details is usually JSON; show its title, or the raw text when it is not. */
function sessionDetail(details: string | null): string {
  if (!details) return '';
  try {
    const d = JSON.parse(details) as Record<string, unknown>;
    return String(d.title ?? d.reason ?? d.topic ?? '');
  } catch {
    return details;
  }
}

function DoingSection({ activity, resources, mindName }: {
  activity: ReturnType<typeof useActivity>;
  resources: DashboardData['resources'] | null;
  mindName: string;
}) {
  const { data: a, isLoading, error, nudge, nudgeLoading, nudgeMessage } = activity;
  const heading = <h3 id="doing-h" className="rail-heading">Doing</h3>;

  if (!a) {
    return (
      <section className="rail-section now-doing" aria-labelledby="doing-h">
        {heading}
        {error ? (
          <p className="rail-failed" role="alert">Could not load activity: {error}</p>
        ) : (
          <p className="rail-meta">{isLoading ? 'Loading activity…' : 'No activity recorded.'}</p>
        )}
      </section>
    );
  }

  const work = a.currentWork;
  const headline = work?.task ?? a.summary;
  const recent = a.recentSessions.slice(0, 5);
  const recentFailed = recent.filter(s => FAILED_OUTCOMES.has(s.outcome ?? '')).length;
  return (
    <section className="rail-section now-doing" data-status={a.statusLabel} aria-labelledby="doing-h">
      <div className="rail-heading-row">
        {heading}
        <span className="status-word">{STATUS_WORD[a.statusLabel] ?? a.statusLabel}</span>
      </div>
      <p className="doing-headline">{headline}</p>
      {work && a.summary && a.summary !== headline && <p className="rail-text">{a.summary}</p>}
      {work?.progress && <p className="rail-text">{work.progress}</p>}
      {work?.detail && <p className="rail-text">{work.detail}</p>}
      <p className="rail-meta">
        {work && `Started ${minutesAgo(Math.round(work.duration / 60))} · `}
        Last action {minutesAgo(a.minutesSinceLastAction)} · {a.stats.sessionsToday} today
        {a.stats.sessionsToday > 0 && `, ${a.stats.successRate}% succeeded`}
      </p>

      {a.stuckState?.isStuck && (
        <div className="doing-stuck" role="alert">
          <strong>Stuck after {a.stuckState.consecutiveErrors ?? 'several'} consecutive errors</strong>
          {a.stuckState.reason && <p>{a.stuckState.reason}</p>}
          {a.stuckState.suggestedFix && <p>Suggested: {a.stuckState.suggestedFix}</p>}
        </div>
      )}
      {error && <p className="rail-failed">Refresh failed ({error}). Showing the last result.</p>}

      {!a.isProductive && a.statusLabel !== 'sleeping' && (
        <button type="button" className="rail-action" onClick={nudge} disabled={nudgeLoading}>
          {nudgeLoading ? 'Sending nudge…' : `Nudge ${mindName} to resume`}
        </button>
      )}
      {nudgeMessage && <p className="rail-text" role="status">{nudgeMessage}</p>}

      {/* Closed by default: long action descriptions otherwise push Feeling and Continuity out of
          reach. The summary keeps the count and any failure visible without opening it. */}
      {recent.length > 0 && (
        <details className="rail-details">
          <summary>
            Recent actions: {recent.length}
            {recentFailed > 0 && <span data-failed>, {recentFailed} failed</span>}
          </summary>
        <ol className="ledger" aria-label="Recent actions">
          {recent.map((s, i) => {
            const detail = sessionDetail(s.details);
            return (
              <li key={s.id ?? i}>
                <span className="ledger-time">{since(s.started_at)}</span>
                <span className="ledger-what">
                  {words(s.action_taken ?? 'unknown')}
                  {detail && <span className="ledger-detail">{detail}</span>}
                </span>
                <span className="ledger-outcome" data-failed={FAILED_OUTCOMES.has(s.outcome ?? '') || undefined}>
                  {s.outcome ? words(s.outcome) : 'Open'}
                </span>
              </li>
            );
          })}
        </ol>
        </details>
      )}

      {resources && (
        <details className="rail-details">
          <summary>Spend today: ${resources.estimatedCostUSD.toFixed(2)}</summary>
          <p className="rail-meta">
            {resources.tokensToday.toLocaleString()} of {resources.dailyBudget.toLocaleString()} tokens
            ({resources.pctOfDailyBudget}%). Resets 00:00 UTC.
          </p>
          <ul className="rail-list">
            {Object.entries(resources.breakdown).sort((x, y) => y[1].pct - x[1].pct).map(([cat, d]) => (
              <li key={cat}><span>{words(cat)}</span><span>{d.pct}% · {d.count} calls</span></li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function until(iso: string | null): string {
  if (!iso) return 'at an unrecorded time';
  const m = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  if (m <= 0) return 'now';
  if (m < 60) return `in ${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `in ${h} h ${m % 60} min` : `in ${Math.floor(h / 24)} d`;
}

/**
 * Doing, when her chat is routed to the runtime (the legacy daemon is paused then). Reads only the runtime's own
 * GET /status (store.mjs status() via worker.ts): lifecycle and autonomy METADATA — never an intention's question,
 * journal content or reply text — and claims no progress or success the status does not report.
 */
function RuntimeDoingSection({ runtime: r }: { runtime: RuntimeStatusView }) {
  const heading = <h3 id="doing-h" className="rail-heading">Doing</h3>;
  if (r.kind !== 'ok') {
    return (
      <section className="rail-section now-doing" data-status="stuck" aria-labelledby="doing-h">
        <div className="rail-heading-row">{heading}<span className="status-word">{r.kind === 'loading' ? 'Checking' : 'Unavailable'}</span></div>
        {r.kind === 'unavailable' && <p className="rail-text">Runtime status: {r.detail}</p>}
      </section>
    );
  }
  const a = r.autonomy;
  const word = r.claimed > 0 ? 'Working' : r.queued > 0 ? 'Waiting'
    : a?.enabled === false ? 'Paused' : a?.enabled === true ? 'Waiting' : 'Unknown';
  const next = a?.nextIntention ? `next intention (${a.nextIntention.source ?? 'unknown source'}) due ${until(a.nextIntention.dueAt)}` : null;
  const autonomyLine = a?.enabled === true ? `Autonomy on · ${next ?? 'no intention scheduled'}`
    : a?.enabled === false ? 'Autonomy is off in the runtime settings'
    : ['Autonomy state not reported by the runtime', next].filter(Boolean).join(' · ');
  const queue = r.claimed + r.queued > 0
    ? `${r.claimed} working · ${r.queued} waiting${r.activeKinds.length ? ` (${r.activeKinds.join(', ')})` : ''}`
    : 'Nothing queued';
  const journal = Object.entries(a?.journal ?? {}).map(([s, n]) => `${n} ${s}`).join(', ');
  return (
    <section className="rail-section now-doing" data-status={word === 'Working' ? 'working' : word === 'Paused' ? 'sleeping' : 'idle'} aria-labelledby="doing-h">
      <div className="rail-heading-row">{heading}<span className="status-word">{word}</span></div>
      <p className="rail-text">{autonomyLine}</p>
      <p className="rail-meta">
        {queue}{a?.episodes != null && ` · ${a.episodes} autonomous episodes recorded`}
      </p>
      <p className="rail-meta">
        Process: {r.containerRunning === false ? 'not running' : r.bootActivity ?? 'no activity reported'} · last heartbeat {since(r.lastHeartbeatAt)}
      </p>
      {journal && <p className="rail-meta">Journal writes: {journal}</p>}
      {a?.journalLastError && <p className="rail-failed" role="alert">Journal write failing: {a.journalLastError}</p>}
      {a?.lastBlockedBy && <p className="rail-failed" role="alert">Last autonomous step blocked: {a.lastBlockedBy}</p>}
      {r.usageBlocked && <p className="rail-failed" role="alert">Model use blocked: {r.usageBlocked}</p>}
    </section>
  );
}

/** One line from the runtime's lifecycle status; nothing when this cortex has no runtime route or it is not Hannah. */
function runtimeLine(r: RuntimeStatusView): string | null {
  const routing = r.kind !== 'loading' && r.kind !== 'absent' && r.routing ? `chat routing ${r.routing === 'enabled' ? 'on' : 'off'}` : null;
  if (r.kind === 'absent') return null;
  if (r.kind === 'loading') return 'checking…';
  if (r.kind === 'unavailable') return [`unavailable: ${r.detail}`, routing].filter(Boolean).join(' · ');
  const history = Object.entries(r.history).map(([status, n]) => `${n} ${status}`).join(', ');
  return [
    r.supervisor ?? 'state unknown',
    r.containerRunning === false ? 'process not running' : null,
    routing,
    `${r.waiting} waiting`,
    history ? `history ${history}` : null,
  ].filter(Boolean).join(' · ');
}

function ContinuitySection({ dashboard, activity, runtime, routed }: {
  dashboard: DashboardData;
  activity: ReturnType<typeof useActivity>['data'];
  runtime: RuntimeStatusView | null;
  routed: boolean;
}) {
  const runtimeText = runtime ? runtimeLine(runtime) : null;
  const { temporal, memoryHealth } = dashboard;
  const commitments = activity?.pendingCommitments ?? [];
  const goals = activity?.topGoals ?? [];
  const queue = activity?.activeQueue ?? [];
  const completions = activity?.recentCompletions ?? [];
  return (
    <section className="rail-section" aria-labelledby="continuity-h">
      <h3 id="continuity-h" className="rail-heading">Continuity</h3>
      <dl className="facts">
        {temporal && (
          <>
            <dt>Age</dt><dd>{temporal.days.toLocaleString()} days</dd>
            <dt>Interactions</dt><dd>{temporal.totalInteractions.toLocaleString()}</dd>
            <dt>Last interaction</dt><dd>{since(temporal.lastInteraction)}</dd>
          </>
        )}
        {memoryHealth && (<><dt>Memories</dt><dd>{memoryHealth.totalMemories.toLocaleString()}</dd></>)}
        {runtimeText && (<><dt>Runtime</dt><dd className="facts-detail">{runtimeText}</dd></>)}
      </dl>

      {commitments.length > 0 && (
        <>
          <h4 className="rail-subheading">Open commitments</h4>
          <ul className="rail-list">
            {commitments.slice(0, 5).map(c => (
              <li key={c.id}><span>{c.content}</span>{c.deadline && <span>due {new Date(c.deadline).toLocaleDateString()}</span>}</li>
            ))}
          </ul>
        </>
      )}

      {goals.length + queue.length + completions.length > 0 && (
        <details className="rail-details">
          <summary>{routed ? 'Earlier daemon goals, queue and work (history, not current)' : 'Goals, queue and completed work'}</summary>
          {goals.length > 0 && <h4 className="rail-subheading">Goals</h4>}
          <ul className="rail-list">
            {goals.slice(0, 5).map(g => <li key={g.id}><span>{g.description}</span><span>{g.progress}%</span></li>)}
          </ul>
          {queue.length > 0 && <h4 className="rail-subheading">Queue ({queue.length})</h4>}
          <ul className="rail-list">
            {queue.slice(0, 5).map(q => (
              <li key={q.id}>
                <span>{words(q.strategy)}: {q.reason}{q.target_repo && ` (${q.target_repo.split('/').pop()})`}</span>
                {q.pr_url ? <a href={q.pr_url} target="_blank" rel="noopener noreferrer">PR</a> : <span>{q.status}</span>}
              </li>
            ))}
          </ul>
          {completions.length > 0 && <h4 className="rail-subheading">Completed</h4>}
          <ul className="rail-list">
            {completions.slice(0, 5).map((c, i) => (
              <li key={i}><span>{c.task}{c.summary && `: ${c.summary}`}</span><span data-failed={c.outcome === 'error' || undefined}>{words(c.outcome)} · {since(c.completedAt)}</span></li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

// ============================================================================
// ALERTS PANEL — persistent alert history with acknowledge/resolve actions
// ============================================================================

const SEVERITY_STYLE: Record<string, { color: string; icon: string }> = {
  crisis: { color: 'var(--status-error)', icon: '🚨' },
  severe: { color: '#F59E0B', icon: '⚠️' },
  warning: { color: '#3B82F6', icon: 'ℹ️' },
  info: { color: 'var(--text-muted)', icon: '📝' },
};

function AlertsPanel({ token, mind, isCreator }: { token: string; mind: ActiveMind; isCreator: boolean }) {
  const { alerts, loading, error, fetchAlerts, acknowledgeAlert, resolveAlert } = useAlerts(token, mind);
  const [filter, setFilter] = useState<string>('');

  if (!isCreator) {
    return (
      <div style={{ padding: 'var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
        Alert management is only visible to the creator.
      </div>
    );
  }

  const filtered = filter ? alerts.filter(a => a.status === filter) : alerts;

  return (
    <div style={{ padding: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {['', 'active', 'acknowledged', 'resolved'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: '2px 10px',
              borderRadius: 12,
              border: '1px solid var(--border-subtle)',
              background: filter === s ? 'var(--surface-active)' : 'transparent',
              color: filter === s ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: 'var(--text-xs)',
              cursor: 'pointer',
            }}
          >
            {s || 'All'}
          </button>
        ))}
        <button onClick={() => fetchAlerts()} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
          ↻ Refresh
        </button>
      </div>

      {loading && <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Loading alerts…</div>}
      {error && <div style={{ color: 'var(--status-error)', fontSize: 'var(--text-sm)' }}>Error: {error}</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: 'var(--space-4)' }}>
          No {filter || ''} alerts
        </div>
      )}

      {filtered.map(alert => {
        const sev = SEVERITY_STYLE[alert.severity] ?? SEVERITY_STYLE.info;
        const time = new Date(alert.fired_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div key={alert.id} style={{
            border: `1px solid ${sev.color}33`,
            borderLeft: `3px solid ${sev.color}`,
            borderRadius: 6,
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--surface-secondary)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                {sev.icon} {alert.emotion_name || alert.signal}
              </span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{time}</span>
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 6 }}>
              {alert.signal} · {alert.severity} · {alert.status}
              {alert.signal_value != null && ` · ${(alert.signal_value * 100).toFixed(0)}%`}
            </div>
            {alert.status === 'active' && (
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button onClick={() => acknowledgeAlert(alert.id)} style={{ fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  Acknowledge
                </button>
                <button onClick={() => resolveAlert(alert.id)} style={{ fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  Resolve
                </button>
              </div>
            )}
            {alert.status === 'acknowledged' && (
              <button onClick={() => resolveAlert(alert.id)} style={{ fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                Resolve
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
